// src/pages/Results.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "../utils/supabaseClient";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";

const Results = ({ darkMode, toggleDarkMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaper = async () => {
      try {
        const { data, error } = await supabase
          .from("papers")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;
        setPaper(data);
      } catch (err) {
        console.error("Error fetching paper:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPaper();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-gray-900">
        <Loader2 className="animate-spin w-8 h-8 text-primary-500" />
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen dark:bg-gray-900 text-gray-300">
        <p>Paper not found.</p>
        <button onClick={() => navigate("/dashboard")} className="btn-primary mt-4">
          Back to Dashboard
        </button>
      </div>
    );
  }

  const outputs = paper.agent_outputs || {};
  const summary = outputs.summary || {};
  const architecture = outputs.architecture || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      <Sidebar />
      <div className="flex-1 p-6">
        <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center mb-6 text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {paper.title || paper.filename}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Uploaded: {new Date(paper.created_at).toLocaleString()}
          </p>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
            {/* Executive Summary */}
            {summary.executive_summary && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Executive Summary
                </h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                  {summary.executive_summary}
                </p>
              </section>
            )}

            {/* Key Findings */}
            {Array.isArray(summary.key_findings) && summary.key_findings.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Key Findings
                </h2>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                  {summary.key_findings.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Methodology */}
            {summary.methodology && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Methodology
                </h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                  {summary.methodology}
                </p>
              </section>
            )}

            {/* Architecture */}
            {architecture.description && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  System Architecture
                </h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                  {architecture.description}
                </p>

                {architecture.mermaid && (
                  <div className="bg-gray-100 dark:bg-gray-900 p-3 mt-3 rounded-md text-sm overflow-x-auto">
                    <pre>{architecture.mermaid}</pre>
                  </div>
                )}
              </section>
            )}

            {/* No Results Case */}
            {!summary.executive_summary &&
              (!paper.agent_outputs ||
                Object.keys(paper.agent_outputs).length === 0) && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <p>AI analysis not available yet. Please check back later.</p>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
